import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@bonusly/analytics';
import { Button, Flex, FormControl, Hide, ModalBody, ModalFooter, Spacer, useToast } from '@bonusly/design-system';

import { BookmarkEditLabels } from '../../components/bookmark-modal/bookmark-edit-labels';
import { BookmarkEditNotes } from '../../components/bookmark-modal/bookmark-edit-notes';
import { useCreateBookmark } from '../../components/bookmark/hooks/use-create-bookmark';
import { useDeleteBookmark } from '../../components/bookmark/hooks/use-delete-bookmark';
import { useUpdateBookmark } from '../../components/bookmark/hooks/use-update-bookmark';
import { useBookmark } from '../../context';
import { BookmarkEvent } from '../../types/event';
import { SuggestionsDisplay } from '../labels-suggestions';

export const BookmarkModalContent: FC = () => {
	const { referenceId, referenceType, notes, onCloseModal, bookmark, labelsIds, suggestions } = useBookmark();
	const { t } = useTranslation('bookmarks');
	const { track } = useAnalytics();

	const toast = useToast();
	const options = {
		onSuccess: (response: any) => {
			let title = t('bookmark_modal.success');

			if (response.config.method === 'delete') {
				track(BookmarkEvent.UnsaveBookmark);
				title = t('bookmark_modal.success_delete');
			}

			onCloseModal();

			toast({
				title,
				isClosable: true,
				status: 'success',
			});
		},
	};
	const { mutate: createBookmark } = useCreateBookmark({ options });

	const { mutate: updateBookmark } = useUpdateBookmark({
		options,
		id: bookmark?.id,
	});

	const { mutate: deleteBookmark } = useDeleteBookmark({
		options,
		id: bookmark?.id,
	});

	const handleDelete = () => {
		if (bookmark?.id) {
			deleteBookmark();
		}
	};
	const handleSubmit = () => {
		if (!bookmark?.id) {
			createBookmark({
				notes,
				reference_id: referenceId,
				reference_type: referenceType,
				label_ids: labelsIds,
				suggestions,
			});
		} else {
			updateBookmark({
				notes,
				reference_id: referenceId,
				reference_type: referenceType,
				label_ids: labelsIds,
				suggestions,
			});
		}
	};
	return (
		<>
			<ModalBody px={5} pt={4}>
				<FormControl>
					<Flex flexDirection="column" gap={3}>
						<BookmarkEditNotes />
						<BookmarkEditLabels />
						<SuggestionsDisplay />
					</Flex>
				</FormControl>
			</ModalBody>
			<ModalFooter>
				<Flex
					alignItems={{ base: 'stretch', xs: 'flex-start' }}
					direction={{ base: 'column', xs: 'row' }}
					gap={3}
					justifyContent="space-between"
					mb={2}
					w="full"
				>
					<Button colorScheme="secondary" variant="alpha" size="md" onClick={handleDelete} isDisabled={!bookmark?.id}>
						{t('bookmark_modal.delete')}
					</Button>
					<Spacer />

					<Hide below="xs">
						<Button variant="ghost" p={2} onClick={onCloseModal} size="md">
							{t('bookmark_modal.cancel')}
						</Button>
					</Hide>
					<Button size="md" onClick={handleSubmit}>
						{t('bookmark_modal.save')}
					</Button>
				</Flex>
			</ModalFooter>
		</>
	);
};
